import React, { useCallback, useEffect, Suspense, useMemo } from 'react';
import {
  PlaidEmbeddedLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkStableEvent,
  PlaidLinkOnEventMetadata,
} from 'react-plaid-link';

import { useObserver } from 'mobx-react-lite';

import { useStores } from 'contexts/stores';
import { useStartAggregatorLinkV2Query } from 'types/graphql';

import {
  Aggregator,
  DUTCHIE_PAY_QUERY_PARMAS_KEY,
  EnrollmentSteps,
  IS_E2E,
  PLAID_STORAGE_KEY,
  PlaidLinkCustomizationNames,
} from 'src/payments/constants';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { usePlaidLinkUtils } from 'src/payments/hooks/use-plaid-link-utils/use-plaid-link-utils';

import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { useIsFirstRender } from 'src/hooks/use-is-first-render';

const MockAggregatorWidget = React.lazy(() => import('./mock-aggregator-widget'));

type PlaidEmbeddedSearchProps = {
  handleEmbeddedDutchiePayLink?: () => void;
  handleOnError?: () => void;
  handleOnLinkExit?: () => void;
  linkCustomizationName?: PlaidLinkCustomizationNames;
  height?: number | string;
  width?: number | string;
  onLoadingStateChange?: (state: { isLoading: boolean; hasToken: boolean }) => void;
  handleOnPlaidConnectBank?: () => void;
};

export const PlaidEmbeddedSearch = ({
  handleEmbeddedDutchiePayLink,
  handleOnError,
  handleOnLinkExit,
  linkCustomizationName = PlaidLinkCustomizationNames.DEFAULT,
  width,
  height = 350,
  onLoadingStateChange,
  handleOnPlaidConnectBank,
}: PlaidEmbeddedSearchProps): JSX.Element => {
  const { User } = useStores();
  const isFirstRender = useIsFirstRender();
  const userFirstName = useObserver(() => User.profile.firstName);
  const userLastName = useObserver(() => User.profile.lastName);
  const { trackPlaidOnEvent, trackEnrollmentStepViewed } = useDutchiePayAnalytics();
  const { aggregatorLinkExited } = useDutchiePayEnrollment();

  const userEmail = useObserver(() => User.email);
  const userPhone = useObserver(() => User.profile.phone);
  const missingRequiredUserInfo = !userFirstName || !userLastName || !userEmail || !userPhone;

  const {
    data: startAggregatorLinkData,
    loading: startAggregatorLinkLoading,
    refetch: refetchStartAggregatorLink,
  } = useStartAggregatorLinkV2Query({
    variables: {
      aggregator: Aggregator.PLAID,
      firstName: userFirstName,
      lastName: userLastName,
      email: userEmail,
      phone: userPhone,
      paymentMethodId: '',
      linkCustomizationName,
    },
    skip: missingRequiredUserInfo,
    fetchPolicy: 'network-only',
  });

  const plaidLinkToken = startAggregatorLinkData?.startAggregatorLinkV2.plaid?.linkToken ?? '';

  const { handleSuccess } = usePlaidLinkUtils({
    handleEmbeddedDutchiePayLink,
    handleOnError,
    linkCustomizationName,
    handleOnPlaidConnectBank,
    refetchStartAggregatorLink,
  });

  /*
   * TODO:
   * Just rely on passing in whatever is passed for handleOnLinkExit
   * and remove the call to useDutchiePayEnrollment and aggregatorLinkExited
   */
  const onLinkExit = handleOnLinkExit ?? aggregatorLinkExited;

  const clearPlaidData = (): void => {
    localStorage.removeItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
    localStorage.removeItem(PLAID_STORAGE_KEY);
  };

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (publicToken, metadata) => {
      void handleSuccess({ publicToken, metadata });
    },
    [handleSuccess]
  );

  const onEvent = useCallback<PlaidLinkOnEvent>(
    (eventName: PlaidLinkStableEvent, metadata: PlaidLinkOnEventMetadata) => {
      trackPlaidOnEvent(eventName, metadata, linkCustomizationName);
    },
    [trackPlaidOnEvent, linkCustomizationName]
  );

  const onExit = useCallback<PlaidLinkOnExit>(() => {
    clearPlaidData();
    onLinkExit();
  }, [onLinkExit]);

  const config = useMemo(
    () => ({
      token: plaidLinkToken,
      onSuccess,
      onEvent,
      onExit,
    }),
    [plaidLinkToken, onSuccess, onEvent, onExit]
  );

  useEffect(() => {
    onLoadingStateChange?.({
      isLoading: startAggregatorLinkLoading,
      hasToken: Boolean(plaidLinkToken),
    });
  }, [startAggregatorLinkLoading, plaidLinkToken, onLoadingStateChange]);

  useEffect(() => {
    if (isFirstRender) {
      trackEnrollmentStepViewed(EnrollmentSteps.connectBank);
    }
  }, [trackEnrollmentStepViewed, isFirstRender]);

  if (IS_E2E) {
    return (
      <Suspense fallback={null}>
        <MockAggregatorWidget aggregator={Aggregator.PLAID} onSuccess={onSuccess} onExit={onExit} />
      </Suspense>
    );
  }

  return (
    <PlaidEmbeddedLink
      {...config}
      style={{
        ...(width && { width }),
        height,
      }}
    />
  );
};

export const MemoizedPlaidEmbeddedSearch = React.memo(PlaidEmbeddedSearch);
